/**
 * ImportModals — Shared import orchestration for ArticleImporter and GlobalUploadModal.
 *
 * Contains:
 *   • UploadModal (primary import picker)
 *   • URL classification (RSS / YouTube / Bilibili routing)
 *   • All secondary modal state + handlers + lazy-loaded modals
 *   • Snippet (Paste Text) modal
 *
 * Props:
 *   - listId: string | null         — target list for created articles
 *   - showUploadModal: boolean      — whether to show the UploadModal
 *   - onCloseUploadModal: () => void — close callback for UploadModal
 *   - snippetMode: 'global' | 'local' — how snippet modal visibility is managed
 *   - onImportSuccess?: () => void  — called after successful import (e.g. clear URL bar)
 *   - fileUpload: object            — from useFileUpload(), needed by UploadModal
 *
 * Ref API:
 *   - addArticleByUrl(url) → boolean — classify URL and create article
 */

import { useState, useCallback, lazy, Suspense, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { toast } from 'sonner';

import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import UploadModal from './UploadModal';

import { parseVideoUrl } from '../utils/videoUtils';
import { isRssUrl } from '../utils/rssUtils';
import { validateArticleUrl } from '../utils/urlValidation';
import {
  ui$,
  createSnippet,
  createPendingArticle,
  createVideoArticle,
  createBatchMergeArticle,
  createPdfArticleFromStorage,
  openModal,
  closeModal,
} from '../store';
import { Dialog } from '../design-system';
import './ImportModals.css';

// ── Lazy-loaded modals (low-frequency import flows) ────────────────────────
const VideoImportModal = lazy(() => import('./VideoImportModal'));
const BilibiliImportModal = lazy(() => import('./BilibiliImportModal'));
const BilibiliCookieModal = lazy(() => import('./BilibiliCookieModal'));
const BatchMergeModal = lazy(() => import('./BatchMergeModal'));
const RssFeedModal = lazy(() => import('./RssFeedModal'));
const ReadwiseImportModal = lazy(() => import('./ReadwiseImportModal'));
const ZoteroImportModal = lazy(() => import('./ZoteroImportModal'));

const ImportModals = forwardRef(function ImportModals({
  listId = null,
  showUploadModal,
  onCloseUploadModal,
  snippetMode = 'local',
  onImportSuccess,
  fileUpload,
}, ref) {
  const { t } = useTranslation('articles');
  const navigate = useNavigate();

  // ── Snippet (Paste Text) ────────────────────────────────────────────────
  // 'global' mode: driven by ui$.activeModal (ArticleImporter on list pages)
  // 'local' mode: internal state (GlobalUploadModal, avoids conflict)
  const globalSnippetVisible = useSelector(() => ui$.activeModal.get() === 'snippet');
  const [localSnippetVisible, setLocalSnippetVisible] = useState(false);
  const showSnippetModal = snippetMode === 'global' ? globalSnippetVisible : localSnippetVisible;
  const closeSnippetModal = snippetMode === 'global'
    ? closeModal
    : () => setLocalSnippetVisible(false);
  const openSnippetModal = snippetMode === 'global'
    ? () => openModal('snippet')
    : () => setLocalSnippetVisible(true);

  const [snippetTitle, setSnippetTitle] = useState('');
  const [snippetContent, setSnippetContent] = useState('');
  const [addingSnippet, setAddingSnippet] = useState(false);

  // ── Video (YouTube) ────────────────────────────────────────────────────
  const [videoImportData, setVideoImportData] = useState(null);

  // ── Bilibili ───────────────────────────────────────────────────────────
  const [bilibiliImportData, setBilibiliImportData] = useState(null);
  const [showBilibiliCookieModal, setShowBilibiliCookieModal] = useState(false);
  const [pendingBilibiliUrl, setPendingBilibiliUrl] = useState(null);
  const [bilibiliCookieError, setBilibiliCookieError] = useState(null);

  // ── Batch merge ────────────────────────────────────────────────────────
  const [showBatchMergeModal, setShowBatchMergeModal] = useState(false);

  // ── RSS ────────────────────────────────────────────────────────────────
  const [showRssFeedModal, setShowRssFeedModal] = useState(false);
  const [rssFeedUrl, setRssFeedUrl] = useState('');

  // ── Readwise ───────────────────────────────────────────────────────────
  const [showReadwiseModal, setShowReadwiseModal] = useState(false);

  // ── Zotero ─────────────────────────────────────────────────────────────
  const [showZoteroModal, setShowZoteroModal] = useState(false);

  // ══════════════════════════════════════════════════════════════════════════
  // URL Processing — detect type and route to appropriate modal
  // ══════════════════════════════════════════════════════════════════════════

  const addArticleByUrl = useCallback((url) => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return false;

    // RSS / Atom feed — bypass URL validation (RSS URLs are valid http/https, checked inside)
    if (isRssUrl(trimmedUrl)) {
      setRssFeedUrl(trimmedUrl);
      setShowRssFeedModal(true);
      return false;
    }

    // YouTube / Bilibili video
    const { platform, videoId } = parseVideoUrl(trimmedUrl);
    if (platform === 'youtube' && videoId) {
      setVideoImportData({ videoId, videoUrl: trimmedUrl });
      return false;
    }
    if (platform === 'bilibili' && videoId) {
      setPendingBilibiliUrl(trimmedUrl);
      setBilibiliCookieError(null);
      setShowBilibiliCookieModal(true);
      return false;
    }

    // Validate URL before creating article — catches invalid input early
    // (avoids articles getting stuck in 'pending' due to bad URL)
    const urlError = validateArticleUrl(trimmedUrl);
    if (urlError) {
      toast.error(urlError);
      return false;
    }

    // Regular URL → pending article
    try {
      createPendingArticle(trimmedUrl, listId);
      toast.success('Article added — fetching content', {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
      return true;
    } catch (err) {
      showErrorAlert('Failed to add article: ' + err.message, err, {
        component: 'ImportModals', action: 'add_article', category: ErrorCategory.IMPORT,
      });
      return false;
    }
  }, [listId, navigate]);

  // ── Expose API via ref ──────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    addArticleByUrl,
  }), [addArticleByUrl]);

  // ══════════════════════════════════════════════════════════════════════════
  // Modal Handlers
  // ══════════════════════════════════════════════════════════════════════════

  // YouTube
  const handleVideoImport = useCallback(async (videoData) => {
    setVideoImportData(null);
    try {
      await createVideoArticle({ ...videoData, listId });
      onImportSuccess?.();
      toast.success('Video added — processing transcript', {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to import video: ' + err.message, err, {
        component: 'ImportModals', action: 'import_youtube', category: ErrorCategory.IMPORT,
      });
    }
  }, [listId, navigate, onImportSuccess]);

  // Bilibili cookie → import
  const handleBilibiliCookieSaved = useCallback(async (cookie) => {
    setShowBilibiliCookieModal(false);
    if (pendingBilibiliUrl) {
      const { videoId } = parseVideoUrl(pendingBilibiliUrl);
      if (videoId) {
        setBilibiliImportData({ videoId, videoUrl: pendingBilibiliUrl, cookie });
      }
      setPendingBilibiliUrl(null);
    }
  }, [pendingBilibiliUrl]);

  const handleBilibiliImport = useCallback(async (videoData) => {
    const coverUrl = videoData.thumbnail
      ? videoData.thumbnail.replace(/^http:\/\//, 'https://')
      : null;
    setBilibiliImportData(null);
    try {
      await createVideoArticle({ ...videoData, coverUrl, listId });
      onImportSuccess?.();
      toast.success('Video added', {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to import video: ' + err.message, err, {
        component: 'ImportModals', action: 'import_bilibili', category: ErrorCategory.IMPORT,
      });
    }
  }, [listId, navigate, onImportSuccess]);

  const handleBilibiliAuthError = useCallback(() => {
    const currentData = bilibiliImportData;
    setBilibiliImportData(null);
    if (currentData) {
      setPendingBilibiliUrl(currentData.videoUrl);
      setBilibiliCookieError('Cookie expired or invalid. Please update your Bilibili cookie.');
      setShowBilibiliCookieModal(true);
    }
  }, [bilibiliImportData]);

  // Batch merge
  const handleBatchMerge = useCallback(async ({ urls, customTitle }) => {
    createBatchMergeArticle(urls, customTitle, listId);
    toast.success('Articles merged — processing', {
      action: { label: 'View All', onClick: () => navigate('/all') },
      duration: 4000,
    });
  }, [listId, navigate]);

  // RSS feed
  const handleRssAddArticle = useCallback(async (articleUrl) => {
    createPendingArticle(articleUrl, listId);
    toast.success('Article added from RSS', {
      action: { label: 'View All', onClick: () => navigate('/all') },
      duration: 4000,
    });
  }, [listId, navigate]);

  // Zotero
  const handleZoteroAddArticle = useCallback(async (articleUrl) => {
    createPendingArticle(articleUrl, listId);
    toast.success('Article added from Zotero', {
      action: { label: 'View All', onClick: () => navigate('/all') },
      duration: 4000,
    });
  }, [listId, navigate]);

  const handleZoteroAddPdf = useCallback(async (storagePath, metadata) => {
    try {
      await createPdfArticleFromStorage(storagePath, metadata, listId);
      toast.success('PDF added from Zotero', {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to import PDF: ' + err.message, err, {
        component: 'ImportModals', action: 'import_zotero_pdf', category: ErrorCategory.IMPORT,
      });
    }
  }, [listId, navigate]);

  // Snippet (Paste Text)
  const handleAddSnippet = useCallback(() => {
    if (!snippetContent.trim()) {
      showErrorAlert('Content is required', null, {
        component: 'ImportModals', action: 'add_snippet_validation', category: ErrorCategory.GENERAL,
      });
      return;
    }
    setAddingSnippet(true);
    try {
      createSnippet({ title: snippetTitle.trim(), content: snippetContent.trim(), listId });
      setSnippetTitle('');
      setSnippetContent('');
      closeSnippetModal();
      toast.success('Note saved', {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to add snippet: ' + err.message, err, {
        component: 'ImportModals', action: 'add_snippet', category: ErrorCategory.IMPORT,
      });
    } finally {
      setAddingSnippet(false);
    }
  }, [snippetTitle, snippetContent, listId, navigate, closeSnippetModal]);

  // ══════════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <>
      {/* ── Upload Modal (primary import picker) ──────────────────────── */}
      {showUploadModal && (
        <UploadModal
          onClose={onCloseUploadModal}
          onAddArticle={(url) => { addArticleByUrl(url); onCloseUploadModal(); }}
          onPdfUpload={(file) => {
            fileUpload.handlePdfUploadFile(file);
            onCloseUploadModal();
          }}
          onEpubUpload={(file) => {
            fileUpload.handleEpubUploadFile(file);
            onCloseUploadModal();
          }}
          onTextFileUpload={(file) => {
            fileUpload.handleTextFileUploadFile(file);
            onCloseUploadModal();
            toast.success(t('textUpload.uploaded'), {
              action: { label: 'View All', onClick: () => navigate('/all') },
              duration: 4000,
            });
          }}
          onAudioUpload={fileUpload.handleAudioUploadFile}
          uploadingAudio={fileUpload.uploadingAudio}
          onOpenSnippet={() => { onCloseUploadModal(); openSnippetModal(); }}
          onOpenBatchMerge={() => { onCloseUploadModal(); setShowBatchMergeModal(true); }}
          onOpenRssFeed={() => { onCloseUploadModal(); setRssFeedUrl(''); setShowRssFeedModal(true); }}
          onOpenReadwise={() => { onCloseUploadModal(); setShowReadwiseModal(true); }}
          onOpenZotero={() => { onCloseUploadModal(); setShowZoteroModal(true); }}
          uploadingPdf={fileUpload.uploadingPdf}
          uploadingEpub={fileUpload.uploadingEpub}
        />
      )}

      {/* ── Paste Text (Snippet) Modal ────────────────────────────────── */}
      <Dialog
        isOpen={showSnippetModal}
        onClose={closeSnippetModal}
        ariaLabel={t('pasteText.title')}
        showClose={false}
        className="snippet-modal"
      >
        <div className="snippet-modal-header">
          <h3>{t('pasteText.title')}</h3>
          <button className="snippet-modal-close" onClick={closeSnippetModal}>×</button>
        </div>
        <input
          type="text"
          className="snippet-title-input"
          placeholder={t('pasteText.titlePlaceholder')}
          value={snippetTitle}
          onChange={e => setSnippetTitle(e.target.value)}
        />
        <textarea
          className="snippet-content-input"
          placeholder={t('pasteText.contentPlaceholder')}
          value={snippetContent}
          onChange={e => setSnippetContent(e.target.value)}
          rows={12}
        />
        <div className="snippet-modal-footer">
          <button className="snippet-cancel" onClick={closeSnippetModal}>
            {t('actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            className="snippet-save"
            onClick={handleAddSnippet}
            disabled={addingSnippet || !snippetContent.trim()}
          >
            {addingSnippet ? t('pasteText.saving') : t('pasteText.save')}
          </button>
        </div>
      </Dialog>

      {/* ── Lazy-loaded secondary modals ──────────────────────────────── */}
      <Suspense fallback={null}>
        {videoImportData && (
          <VideoImportModal
            videoId={videoImportData.videoId}
            videoUrl={videoImportData.videoUrl}
            onImport={handleVideoImport}
            onClose={() => setVideoImportData(null)}
          />
        )}

        {showBilibiliCookieModal && (
          <BilibiliCookieModal
            onCookieSaved={handleBilibiliCookieSaved}
            onClose={() => { setShowBilibiliCookieModal(false); setPendingBilibiliUrl(null); setBilibiliCookieError(null); }}
            error={bilibiliCookieError}
          />
        )}

        {bilibiliImportData && (
          <BilibiliImportModal
            videoId={bilibiliImportData.videoId}
            videoUrl={bilibiliImportData.videoUrl}
            cookie={bilibiliImportData.cookie}
            onImport={handleBilibiliImport}
            onClose={() => setBilibiliImportData(null)}
            onCookieError={handleBilibiliAuthError}
          />
        )}

        {showBatchMergeModal && (
          <BatchMergeModal
            onMerge={handleBatchMerge}
            onClose={() => setShowBatchMergeModal(false)}
          />
        )}

        {showRssFeedModal && (
          <RssFeedModal
            initialUrl={rssFeedUrl}
            onAddArticle={handleRssAddArticle}
            onClose={() => { setShowRssFeedModal(false); setRssFeedUrl(''); onImportSuccess?.(); }}
          />
        )}

        {showReadwiseModal && (
          <ReadwiseImportModal
            onAddArticle={handleRssAddArticle}
            onClose={() => setShowReadwiseModal(false)}
          />
        )}

        {showZoteroModal && (
          <ZoteroImportModal
            onAddArticle={handleZoteroAddArticle}
            onAddPdfFromStorage={handleZoteroAddPdf}
            onClose={() => setShowZoteroModal(false)}
          />
        )}
      </Suspense>
    </>
  );
});

export default ImportModals;
